import { useState, useMemo } from "react"
import { useTranslation } from 'react-i18next'
import { useFotwFeed } from "@/hooks/useFotwFeed"
import { FotwLeaderboard } from "@/components/fotw/FotwLeaderboard"
import { FotwGallery } from "@/components/fotw/FotwGallery"
import { getCurrentWeekIndex, getTimeLeftInWeek, getWeekProgress, isVotingEnded } from "@/lib/utils"

export default function FlashOfTheWeekPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'gallery'>('gallery')
  

  const getProgressColor = (progress: number) => {
    // Convert progress to a value between 0 and 1
    const value = progress / 100
    
    // Define color stops
    const colors = {
      green: { r: 34, g: 197, b: 94 }, // text-green-500
      yellow: { r: 234, g: 179, b: 8 }, // text-yellow-500
      red: { r: 239, g: 68, b: 68 }     // text-red-500
    }
    
    let r, g, b
    if (value <= 0.5) {
      // Interpolate between green and yellow
      const factor = value * 2
      r = colors.green.r + (colors.yellow.r - colors.green.r) * factor
      g = colors.green.g + (colors.yellow.g - colors.green.g) * factor
      b = colors.green.b + (colors.yellow.b - colors.green.b) * factor
    } else {
      // Interpolate between yellow and red
      const factor = (value - 0.5) * 2
      r = colors.yellow.r + (colors.red.r - colors.yellow.r) * factor
      g = colors.yellow.g + (colors.red.g - colors.yellow.g) * factor
      b = colors.yellow.b + (colors.red.b - colors.yellow.b) * factor
    }
    
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`
  }
  
  const currentWeekIndex = getCurrentWeekIndex()
  const timeLeft = getTimeLeftInWeek()
  const weekProgress = getWeekProgress()
  const votingEnded = isVotingEnded()
  const { items, loading, error } = useFotwFeed(currentWeekIndex)

  // Shuffle items for gallery view
  const shuffledItems = useMemo(() => {
    if (!items || items.length === 0) return items
    const shuffled = [...items]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [items])

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="sticky top-0 z-50 bg-background">
          <div className="phone-container-padded pt-4 pb-2">
            <div className="mb-4">
              <div className="mb-3">
                <h1 className="text-page-title text-foreground leading-tight mb-3 whitespace-pre-line">
                  Flash of the Week Amsterdam 🇳🇱
                  {t('fotw.title')}
                </h1>
              </div>
              
              {/* Week Progress Bar */}
              <div className="mb-4">
                <div className="relative w-full bg-gray-100 rounded-full h-[34px] p-[5px] mb-2">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${weekProgress}%`,
                      backgroundColor: getProgressColor(weekProgress)
                    }}
                  />
                </div>
                <div className="text-center">
                  {votingEnded ? (
                    <span className="text-sm font-medium text-destructive">
                      ✓ Voting has ended - Results are final
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">
                      {t('fotw.weekProgress', { 
                        weekIndex: currentWeekIndex, 
                        daysLeft: timeLeft.days,
                        hoursLeft: timeLeft.hours,
                        minutesLeft: timeLeft.minutes
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-muted rounded-lg p-1 mb-4">
              <button
                onClick={() => setActiveTab('gallery')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'gallery'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('fotw.tabs.gallery')}
              </button>
              <button
                onClick={() => setActiveTab('leaderboard')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'leaderboard'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('fotw.tabs.leaderboard')}
              </button>
            </div>
          </div>
        </div>
        
        <div className="phone-container-padded">
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
            </div>
          )}
          
          {!!error && (
            <div className="flex justify-center items-center py-12">
              <p className="text-destructive">{t('fotw.error')}</p>
            </div>
          )}
          
          {!loading && !error && (
            <>
              {activeTab === 'leaderboard' && (
                <FotwLeaderboard items={items} weekIndex={currentWeekIndex} />
              )}
              {activeTab === 'gallery' && (
                <FotwGallery items={shuffledItems} weekIndex={currentWeekIndex} votingEnded={votingEnded} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
} 